export const CSV_COLUMNS = [
  'date', 'branchName', 'tankProduct', 'tankNumber',
  'openingLevel', 'closingLevel', 'supplyReceived', 'pricePerLiter', 'excessPer20L',
  'pump1Opening', 'pump1Closing',
  'pump2Opening', 'pump2Closing',
  'pump3Opening', 'pump3Closing',
  'pump4Opening', 'pump4Closing',
  'expense1Desc', 'expense1Amount',
  'expense2Desc', 'expense2Amount',
  'expense3Desc', 'expense3Amount',
]

// Product names MUST match the tank's product in the app exactly (case-insensitive).
// Tanks are created as PMS / AGO / DPK / LPG, so the template uses those — NOT the
// legacy Petrol/Diesel names, which would fail to match and skip every row.
export const CSV_TEMPLATE = [
  CSV_COLUMNS.join(','),
  '2026-04-20,Downtown Branch,PMS,1,35000,22000,0,200,1,120000,126500,95000,101500,,,,,' +
    'Staff Salaries,45000,Generator Fuel,8000,,',
  '2026-04-20,Downtown Branch,AGO,1,28000,18500,0,220,1,75000,84500,,,,,,,' +
    'Security,10000,,,,',
].join('\n')

function parseCSVLine(line) {
  const result = []
  let inQuotes = false
  let current = ''
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (ch === '"') {
      inQuotes = !inQuotes
    } else if (ch === ',' && !inQuotes) {
      result.push(current.trim())
      current = ''
    } else {
      current += ch
    }
  }
  result.push(current.trim())
  return result
}

export function parseCSV(text) {
  const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n')
    .split('\n').map((l) => l.trim()).filter(Boolean)

  if (lines.length < 2) {
    return { rows: [], error: 'CSV must have a header row and at least one data row.' }
  }

  const headers = parseCSVLine(lines[0])
  const rows = lines.slice(1).map((line, i) => {
    const values = parseCSVLine(line)
    const raw = {}
    headers.forEach((h, idx) => { raw[h] = values[idx] ?? '' })
    return { lineNumber: i + 2, raw }
  })

  return { rows, error: null }
}

export function validateAndTransformRow(row, branches) {
  const { raw, lineNumber } = row
  const errors = []

  // Required fields
  if (!raw.date || !/^\d{4}-\d{2}-\d{2}$/.test(raw.date.trim())) {
    errors.push('date must be YYYY-MM-DD format')
  }

  const openingLevel = parseFloat(raw.openingLevel)
  const closingLevel = parseFloat(raw.closingLevel)
  const supplyReceived = parseFloat(raw.supplyReceived ?? 0)
  const pricePerLiter = parseFloat(raw.pricePerLiter)
  const excessPer20L = parseFloat(raw.excessPer20L || 1)
  const tankNumber = parseInt(raw.tankNumber)

  if (isNaN(openingLevel)) errors.push('openingLevel must be a number')
  if (isNaN(closingLevel)) errors.push('closingLevel must be a number')
  if (isNaN(supplyReceived)) errors.push('supplyReceived must be a number')
  if (isNaN(pricePerLiter)) errors.push('pricePerLiter must be a number')
  if (isNaN(tankNumber)) errors.push('tankNumber must be a number')

  // Find branch (case-insensitive)
  const branch = branches.find(
    (b) => b.name.toLowerCase() === (raw.branchName || '').trim().toLowerCase()
  )
  if (!branch) {
    errors.push(`Branch "${raw.branchName || ''}" not found`)
    return { lineNumber, errors, tankId: null, entry: null, meta: raw }
  }

  // Find tank
  const tank = branch.tanks.find(
    (t) =>
      t.tankNumber === tankNumber &&
      t.product.toLowerCase() === (raw.tankProduct || '').trim().toLowerCase()
  )
  if (!tank) {
    errors.push(
      `No ${raw.tankProduct} Tank #${tankNumber} found in "${branch.name}"`
    )
    return { lineNumber, errors, tankId: null, entry: null, meta: raw }
  }

  if (errors.length > 0) {
    return { lineNumber, errors, tankId: null, entry: null, meta: raw }
  }

  // Pumps are stored at the BRANCH level, grouped by product (not nested under a
  // tank). Map each filled CSV slot (pump1..4) to the matching branch pump for
  // this tank's product, and carry its real id so pump_readings can be inserted.
  const productPumps = (branch.pumps || []).filter(
    (p) => (p.product || '').toLowerCase() === (tank.product || '').toLowerCase()
  )
  const pumps = []
  for (let i = 1; i <= 4; i++) {
    const o = raw[`pump${i}Opening`]
    const c = raw[`pump${i}Closing`]
    if (o && c && o.trim() !== '' && c.trim() !== '') {
      const opening = parseFloat(o)
      const closing = parseFloat(c)
      if (isNaN(opening) || isNaN(closing)) continue
      const pumpDef = productPumps[i - 1]
      if (!pumpDef) {
        errors.push(`pump${i} has a reading but "${branch.name}" has no ${tank.product} pump #${i}. Add it first, or clear that column.`)
        continue
      }
      pumps.push({
        pumpId: pumpDef.id,
        openingMeter: opening,
        closingMeter: closing,
        excessPer20L,
      })
    }
  }

  if (errors.length > 0) {
    return { lineNumber, errors, tankId: null, entry: null, meta: raw }
  }
  if (pumps.length === 0) {
    errors.push('At least one pump reading (pump1Opening + pump1Closing) is required')
    return { lineNumber, errors, tankId: null, entry: null, meta: raw }
  }

  // Build expenses array
  const expenses = []
  for (let i = 1; i <= 3; i++) {
    const desc = (raw[`expense${i}Desc`] || '').trim()
    const amount = parseFloat(raw[`expense${i}Amount`] || 0)
    if (desc && !isNaN(amount) && amount > 0) {
      expenses.push({ description: desc, amount })
    }
  }

  // Shaped for useDailyData.importEntry (a completed day + its children).
  const entry = {
    branchId: branch.id,
    product: tank.product,
    date: raw.date.trim(),
    pricePerLiter,
    tanks: [{ tankId: tank.id, openingLevel, closingLevel, supplyReceived }],
    pumps,
    expenses,
  }

  return {
    lineNumber,
    errors: [],
    tankId: tank.id,
    entry,
    meta: {
      branchName: branch.name,
      tankProduct: tank.product,
      tankNumber: tank.tankNumber,
    },
  }
}

export function triggerCSVDownload(filename, content) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}
